import type {
  AssistantSurface,
  ApiAssistantPickedCar,
  ApiAssistantSuggestedAction,
} from 'src/types/assistant';

/**
 * The tab's copy of the assistant's conversations (app#397): one per catalog, kept in
 * `sessionStorage`, so a conversation outlives a visit to a car's page, Back and a reload, and ends
 * with the tab. It belongs to whoever held the storefront when it was written — an account, or the
 * visitor who has not signed in — and is never shown to anybody else (`owner`).
 *
 * What is read back is the browser's, not ours: an extension or the person may have rewritten it.
 * So it is read as untrusted data. Its shape is checked field by field — the ids that build a link
 * to a car must be listing ids, the dates of "Book these dates" dates, the point of "Prepare the
 * offer" a point's id — and anything that does not fit gives a clean sheet, never an error. The
 * words are only ever shown as text. The messages left for the day are not kept at all: the api
 * counts them.
 */

/** One line of the conversation: what the person wrote, or what the assistant answered. */
export interface ConversationEntry {
  id: number;
  role: 'person' | 'assistant';
  text: string;
  cars: ApiAssistantPickedCar[];
  action: ApiAssistantSuggestedAction;
}

/** A conversation as it is kept: its lines, and the id the api gave it to continue. */
export interface StoredConversation {
  entries: ConversationEntry[];
  conversationId?: string;
  /** What the person had typed and not had answered when the tab was reloaded. */
  draft?: string;
}

export type StoredConversations = Partial<Record<AssistantSurface, StoredConversation>>;

export const CONVERSATIONS_KEY = 'gac.assistant.conversations';

/** The most a message may hold — the api's own bound. */
export const ASSISTANT_TEXT_LIMIT = 1000;
const VERSION = 1;

/** The bounds of what is believed: no conversation of the api is longer, no message wider. */
const MAX_ENTRIES = 200;
const MAX_TEXT = 20_000;
const MAX_ID = 200;
const MAX_CARS = 3;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown, max = MAX_TEXT): value is string =>
  typeof value === 'string' && value.length <= max;
const isOptionalText = (value: unknown): boolean =>
  value === undefined || value === null || isText(value);
const isNumberOrNull = (value: unknown): boolean =>
  value === null || (typeof value === 'number' && Number.isFinite(value));

/** A car of a reply, as far as the panel builds links and cards from it. */
const isPick = (value: unknown, surface: AssistantSurface): value is ApiAssistantPickedCar => {
  if (!isObject(value) || !isObject(value.car)) return false;

  const { car } = value;

  if (
    typeof value.listingId !== 'string' ||
    !UUID.test(value.listingId) ||
    car.listingId !== value.listingId
  )
    return false;
  if (!isText(value.reason) || !(value.generation === undefined || isText(value.generation)))
    return false;
  if (
    !isText(car.title, MAX_ID * 2) ||
    !isOptionalText(car.image) ||
    !isNumberOrNull(car.year ?? null)
  )
    return false;

  if (surface === 'buy') {
    return (
      car.vertical === 'buy' &&
      Number.isFinite(car.price) &&
      isText(car.currency, 10) &&
      isText(car.sellerId, MAX_ID) &&
      (car.evidence === null || isObject(car.evidence))
    );
  }

  return (
    car.vertical === 'rent' &&
    isNumberOrNull(car.dailyPrice ?? null) &&
    isOptionalText(car.location)
  );
};

/** What a reply asked of the panel — the action its buttons take. */
const isAction = (value: unknown): value is ApiAssistantSuggestedAction => {
  if (!isObject(value)) return false;

  switch (value.type) {
    case 'none':
      return true;
    case 'ask':
      return Array.isArray(value.missing) && value.missing.every((item) => isText(item, MAX_ID));
    case 'book':
      return (
        isObject(value.dates) &&
        DATE.test(String(value.dates.startDate)) &&
        DATE.test(String(value.dates.endDate))
      );
    case 'prepareOffer':
      return (
        (value.deliveryPointId === null ||
          (typeof value.deliveryPointId === 'string' && UUID.test(value.deliveryPointId))) &&
        (value.deliveryCity === null || isText(value.deliveryCity, MAX_ID))
      );
    default:
      return false;
  }
};

const readConversation = (value: unknown, surface: AssistantSurface): StoredConversation | null => {
  if (!isObject(value) || !Array.isArray(value.entries) || value.entries.length > MAX_ENTRIES)
    return null;
  if (!(value.conversationId === undefined || isText(value.conversationId, MAX_ID))) return null;

  const entries: ConversationEntry[] = [];

  for (const entry of value.entries) {
    if (
      !isObject(entry) ||
      (entry.role !== 'person' && entry.role !== 'assistant') ||
      !isText(entry.text)
    )
      return null;
    if (!Array.isArray(entry.cars) || entry.cars.length > MAX_CARS || !isAction(entry.action))
      return null;
    // A person's line carries words only.
    if (entry.role === 'person' && (entry.cars.length > 0 || entry.action.type !== 'none'))
      return null;
    if (!entry.cars.every((pick) => isPick(pick, surface))) return null;

    entries.push({
      id: entries.length + 1,
      role: entry.role,
      text: entry.text,
      cars: entry.cars as ApiAssistantPickedCar[],
      action: entry.action,
    });
  }

  // A message the tab was reloaded under was never answered here: it goes back into the field, so
  // it is not typed twice and not shown as if it had been asked and left without an answer.
  let draft: string | undefined;

  while (entries.length > 0 && entries[entries.length - 1].role === 'person') {
    draft = entries.pop()!.text;
  }

  return {
    entries,
    conversationId: entries.length > 0 ? (value.conversationId as string | undefined) : undefined,
    // A draft is a message to send: no longer than one may be.
    ...(draft ? { draft: draft.slice(0, ASSISTANT_TEXT_LIMIT) } : {}),
  };
};

/**
 * The conversations a stored record holds for `owner` — none when it is someone else's, of another
 * shape, or not JSON at all. A part that does not fit is dropped with its catalog's conversation.
 */
export const parseConversations = (raw: string | null, owner: string): StoredConversations => {
  if (!raw) return {};

  let record: unknown;

  try {
    record = JSON.parse(raw);
  } catch {
    return {};
  }

  if (
    !isObject(record) ||
    record.v !== VERSION ||
    record.owner !== owner ||
    !isObject(record.conversations)
  )
    return {};

  const kept: StoredConversations = {};

  for (const surface of ['buy', 'rent'] as const) {
    const conversation = readConversation(record.conversations[surface], surface);

    if (conversation && (conversation.entries.length > 0 || conversation.draft))
      kept[surface] = conversation;
  }

  return kept;
};

/** The record of `owner`'s conversations, as written to the tab's storage. */
export const serializeConversations = (owner: string, conversations: StoredConversations): string =>
  JSON.stringify({
    v: VERSION,
    owner,
    conversations: Object.fromEntries(
      Object.entries(conversations).map(([surface, conversation]) => [
        surface,
        { entries: conversation!.entries, conversationId: conversation!.conversationId },
      ])
    ),
  });

/** The tab's storage, or `null` where the browser keeps none or refuses it. */
const tabStore = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** What the tab keeps for `owner`. A record of anybody else, or one that does not read, is removed. */
export const loadConversations = (owner: string): StoredConversations => {
  try {
    const store = tabStore();
    const raw = store?.getItem(CONVERSATIONS_KEY) ?? null;
    const kept = parseConversations(raw, owner);

    if (raw && Object.keys(kept).length === 0) store?.removeItem(CONVERSATIONS_KEY);

    return kept;
  } catch {
    return {};
  }
};

export const saveConversations = (owner: string, conversations: StoredConversations): void => {
  try {
    const store = tabStore();
    const any = Object.values(conversations).some(
      (conversation) => conversation && conversation.entries.length > 0
    );

    if (!any) store?.removeItem(CONVERSATIONS_KEY);
    else store?.setItem(CONVERSATIONS_KEY, serializeConversations(owner, conversations));
  } catch {
    // A full or refused storage keeps the conversation for this page only.
  }
};

export const forgetConversations = (): void => {
  try {
    tabStore()?.removeItem(CONVERSATIONS_KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
};
